import {
  BookOpen,
  Bot,
  Coins,
  Compass,
  Dices,
  InfoIcon,
  Layers,
  MessageCircle,
  Radio,
  ShieldCheck,
  Wallet,
  Waypoints,
  Zap,
} from 'lucide-react'

// ===== Site Metadata =====
export const SITE_NAME = 'Supra'
export const SITE_SHORT_NAME = 'Supra Docs'
export const SITE_TITLE_TEMPLATE = '%s | Supra Docs'
export const SITE_DEFAULT_TITLE = 'Supra Documentation'
export const SITE_DESCRIPTION =
  'Guides, references, and tutorials for building on Supra — the vertically integrated Layer 1 with native oracles, dVRF, and automation.'
export const SITE_URL = process.env.NEXT_PUBLIC_APP_URL || 'https://docs.supra.com'
export const SITE_LOCALE = 'en_US'
export const SUPRA_SITE_URL = 'https://supra.com'
export const X_HANDLE = '@SUPRA_Labs'

// ===== Social share card =====
// Served by the `src/app/opengraph-image.png` file convention. Declared explicitly
// because the per-page `openGraph` in the catch-all route replaces the inherited
// object wholesale — Next.js does not deep-merge it, so images must be restated.
export const OG_IMAGE = {
  url: '/opengraph-image.png',
  width: 1200,
  height: 630,
  type: 'image/png',
  alt: 'Supra Documentation — build on the Supra Layer 1',
}

export const OPEN_GRAPH = {
  type: 'website',
  siteName: SITE_SHORT_NAME,
  locale: SITE_LOCALE,
  images: [OG_IMAGE],
}

export const TWITTER_CARD = {
  card: 'summary_large_image',
  site: X_HANDLE,
  creator: X_HANDLE,
  images: [OG_IMAGE],
}

// ===== Branding =====
export const SUPRA_LOGO_DARK = '/images/logo/Supra-Red-Dark.svg'
export const SUPRA_LOGO_LIGHT = '/images/logo/Supra-Red-Light.svg'
export const SUPRA_LOGO_SMALL_ICON = '/images/logo/supra-logo-small.svg'

export const STARKEY_LOGO_DARK = '/images/logo/starKey-logo-dark.svg'
export const STARKEY_LOGO_LIGHT = '/images/logo/starKey-logo-light.svg'
export const STARKEY_LOGO_SMALL_DARK = '/images/logo/starKey-logo-small-dark.svg'
export const STARKEY_LOGO_SMALL_LIGHT = '/images/logo/starKey-logo-small-light.svg'

export const DEFAULT_ROUTE = '/network'

// ===== Theme Colors =====
export const THEME = {
  color: {
    hue: { light: 0, dark: 0 },
    saturation: { light: 85, dark: 90 },
    lightness: { light: 45, dark: 50 },
  },
  backgroundColor: {
    dark: '#111111',
    light: '#fafafa',
  },
}

// ===== External Links =====
export const EXTERNAL_LINKS = {
  discord: 'https://discord.gg/supralabs',
  suprascan: 'https://suprascan.io',
  starkey: 'https://starkey.app',
  devHub: 'https://hub.supra.com/',
  githubSupraLabs: 'https://github.com/Supra-Labs',
  githubEntropy: 'https://github.com/Entropy-Foundation',
  whitepapers: 'https://supra.com/whitepapers/',
  bugBounty: 'https://supra.com/bug-bounty/',
  securityAudits: 'https://supra.com/security-audits/',
  privacyPolicy: 'https://supra.com/privacy-policy/',
  terms: 'https://supra.com/terms/',
  docsRepo: 'https://github.com/Entropy-Foundation/supra-documentation/tree/master',
}

// ===== Banner =====
export const BANNER = {
  storageKey: 'supra-discord-banner',
  text: (
    <div className="flex items-center gap-2 justify-center relative z-40 group hover:underline-offset-0">
      <InfoIcon className="text-muted-foreground/80 size-4" strokeWidth={2.5} /> Join our{' '}
      <span>
        <span className="text-supra-red-accent underline underline-offset-[0.1875rem] group-hover:underline-supra-red">
          Discord server!{' '}
        </span>
        <span className="text-supra-red-accent underline-offset-0">↗</span>
      </span>
    </div>
  ),
  href: 'https://discord.gg/supralabs',
}

// ===== Header Links (beside search bar) =====
export const HEADER_LINKS = [
  { label: 'SupraScan Explorer', href: 'https://suprascan.io', external: true },
  { label: 'StarKey Wallet', href: 'https://starkey.app', external: true },
  // No room beside the logo between md and lg; the footer and Useful Links page still carry it there.
  { label: 'StarKey Docs', href: 'https://docs.starkey.app', external: true, className: 'hidden lg:block' },
  { label: 'Discord', href: 'https://discord.gg/supralabs', external: true },
]

// ===== Header Mobile Links =====
export const HEADER_MOBILE_LINKS = [
  { label: 'Supra Layer 1', href: '/network', external: false, icon: Layers },
  { label: 'Automation', href: '/automation', external: false, icon: Zap },
  { label: 'Oracles', href: '/oracles/overview', external: false, icon: Radio },
  { label: 'dVRF', href: '/dvrf/overview', external: false, icon: Dices },
  { label: 'Supranova(Bridge)', href: '/supranova', external: false, icon: Waypoints },
  { label: 'iAssets', href: '/proof-of-efficient-liquidity-poel/overview', external: false, icon: Coins },
  { label: 'AI Agents', href: '/ai-agents', external: false, icon: Bot },
  { label: 'Audit Reports', href: '/audit-reports', external: false, icon: ShieldCheck },
  { label: 'SupraScan Explorer', href: 'https://suprascan.io', external: true, icon: Compass },
  { label: 'StarKey Wallet', href: 'https://starkey.app', external: true, icon: Wallet },
  { label: 'StarKey Docs', href: 'https://docs.starkey.app', external: true, icon: BookOpen },
  { label: 'Discord', href: 'https://discord.gg/supralabs', external: true, icon: MessageCircle },
]

// ===== Footer =====
export const FOOTER_BRAND = {
  tagline: 'Building the future of decentralized finance.',
}

export const FOOTER_COLUMNS = [
  {
    title: 'Links',
    links: [
      { label: 'Whitepapers', href: 'https://supra.com/whitepapers/' },
      { label: 'Bug Bounty', href: 'https://supra.com/bug-bounty/' },
      { label: 'Security Audits', href: 'https://github.com/Entropy-Foundation/security-audits' },
    ],
  },
  {
    title: 'Developers',
    links: [
      { label: 'Supra Dev Hub', href: 'https://github.com/supra-labs/supra-dev-hub/tree/main' },
      { label: 'Supra Labs Github', href: 'https://github.com/Supra-Labs' },
      { label: 'Entropy Foundation Github', href: 'https://github.com/Entropy-Foundation' },
      { label: 'StarKey Docs', href: 'https://docs.starkey.app' },
    ],
  },
]

export const FOOTER_BOTTOM_LINKS = [
  { label: 'Privacy', href: 'https://supra.com/privacy-policy/' },
  { label: 'Terms', href: 'https://supra.com/terms-of-use/' },
]

// ===== Sidebar Config =====
export const SIDEBAR_CONFIG = {
  defaultMenuCollapseLevel: 1,
  toggleButton: true,
  autoCollapse: true,
}

// ===== TOC Config =====
export const TOC_CONFIG = {
  float: true,
  title: 'On This Page',
}

// ===== Sidebar Select Sections =====
// ===== SEO: section labels folded into <title> =====
// Longest path first — the first match wins, so /network/move must precede /network.
// Separate from SIDEBAR_SECTIONS on purpose: that list's labels are dropdown copy
// ('Overview' for /network), which is useless inside a page title.
export const SECTION_LABELS = [
  { path: '/network/move', label: 'MoveVM' },
  { path: '/network/evm', label: 'SupraEVM' },
  { path: '/network/node', label: 'Node Operator' },
  { path: '/network', label: 'Supra L1' },
  { path: '/automation', label: 'Automation' },
  { path: '/oracles', label: 'Oracles' },
  { path: '/dvrf', label: 'dVRF' },
  { path: '/supranova', label: 'SupraNova' },
  { path: '/proof-of-efficient-liquidity-poel', label: 'iAssets' },
  { path: '/ai-agents', label: 'AI Agents' },
  { path: '/audit-reports', label: 'Audit Reports' },
]

// Titles that occur on more than one route — the same topic documented once per VM or per
// section. Google saw 11 pages called "Overview" and 9 called "Getting Started"; these get
// the section label folded in. Seeded from the built <title> tags, so re-check with:
//   grep -h '<title>' .next/server/app/**/*.html | sort | uniq -d
export const AMBIGUOUS_PAGE_TITLES = new Set([
  'Available Networks',
  'CLI Improvements',
  'Create your subscription',
  'Deposit and Withdraw Funds',
  'Download the Node Management Scripts',
  'Foundry',
  'Gas Configurations',
  'Getting Private Key from StarKey',
  'Getting Started',
  'Hardhat',
  'Monitor Node Synchronization',
  'Network Information',
  'Overview',
  'Prerequisites',
  'Recovery Guide',
  'Remix IDE',
  'Request Random Numbers',
  'Smart Contract Integration',
  'Start Building',
  'Update the Supra Node Docker Image',
  'Upgrade Checklist (Pre-Upgrade Verification)',
  'Write Smart Contract',
])

export const SIDEBAR_SECTIONS = [
  { label: 'Overview', path: '/network' },
  { label: 'MoveVM', path: '/network/move' },
  { label: 'SupraEVM', path: '/network/evm' },
  { label: 'Node Operator', path: '/network/node' },
]

export const SIDEBAR_SECTION_PATHS = SIDEBAR_SECTIONS.map((s) => s.path)

export function getSidebarSectionForPath(pathname) {
  // Treat root path as /network (which is what middleware rewrites it to)
  if (pathname === '/') pathname = '/network'

  if (!pathname) return SIDEBAR_SECTIONS[0]

  const sortedSections = [...SIDEBAR_SECTIONS].sort((a, b) => b.path.length - a.path.length)
  return sortedSections.find((section) => pathname.startsWith(section.path)) || SIDEBAR_SECTIONS[0]
}
